import React, { useState } from 'react'
import ReactDOM from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import ImageCropUploader from './ImageCropUploader';
import { useDispatch } from 'react-redux';
import { registerVendorUser } from '../state/slices/vendorSlice';
import { checkFieldsForProfanity } from '../helper/profanityFilter';
import { TERMS_TEXT } from './TermsModal';

const US_STATES = [
    { value: 'AL', label: 'Alabama' }, { value: 'AK', label: 'Alaska' },
    { value: 'AZ', label: 'Arizona' }, { value: 'AR', label: 'Arkansas' },
    { value: 'CA', label: 'California' }, { value: 'CO', label: 'Colorado' },
    { value: 'CT', label: 'Connecticut' }, { value: 'DE', label: 'Delaware' },
    { value: 'DC', label: 'District of Columbia' }, { value: 'FL', label: 'Florida' },
    { value: 'GA', label: 'Georgia' }, { value: 'HI', label: 'Hawaii' },
    { value: 'ID', label: 'Idaho' }, { value: 'IL', label: 'Illinois' },
    { value: 'IN', label: 'Indiana' }, { value: 'IA', label: 'Iowa' },
    { value: 'KS', label: 'Kansas' }, { value: 'KY', label: 'Kentucky' },
    { value: 'LA', label: 'Louisiana' }, { value: 'ME', label: 'Maine' },
    { value: 'MD', label: 'Maryland' }, { value: 'MA', label: 'Massachusetts' },
    { value: 'MI', label: 'Michigan' }, { value: 'MN', label: 'Minnesota' },
    { value: 'MS', label: 'Mississippi' }, { value: 'MO', label: 'Missouri' },
    { value: 'MT', label: 'Montana' }, { value: 'NE', label: 'Nebraska' },
    { value: 'NV', label: 'Nevada' }, { value: 'NH', label: 'New Hampshire' },
    { value: 'NJ', label: 'New Jersey' }, { value: 'NM', label: 'New Mexico' },
    { value: 'NY', label: 'New York' }, { value: 'NC', label: 'North Carolina' },
    { value: 'ND', label: 'North Dakota' }, { value: 'OH', label: 'Ohio' },
    { value: 'OK', label: 'Oklahoma' }, { value: 'OR', label: 'Oregon' },
    { value: 'PA', label: 'Pennsylvania' }, { value: 'RI', label: 'Rhode Island' },
    { value: 'SC', label: 'South Carolina' }, { value: 'SD', label: 'South Dakota' },
    { value: 'TN', label: 'Tennessee' }, { value: 'TX', label: 'Texas' },
    { value: 'UT', label: 'Utah' }, { value: 'VT', label: 'Vermont' },
    { value: 'VA', label: 'Virginia' }, { value: 'WA', label: 'Washington' },
    { value: 'WV', label: 'West Virginia' }, { value: 'WI', label: 'Wisconsin' },
    { value: 'WY', label: 'Wyoming' },
];

const VendorRegistration = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [showTerms, setShowTerms] = useState(false);
    const [termsChecked, setTermsChecked] = useState(false);

    const [vendorData, setVendorData] = useState({
        vendor_name: "",
        vendor_description: "",
        first_name: "",
        last_name: "",
        email: "",
        password: "",
        confirmPassword: "",
        address: "",
        apt_number: "",
        city: "",
        state: "",
        zipcode: "",
        mailing_address: "",
        mailing_apt_number: "",
        mailing_city: "",
        mailing_state: "",
        mailing_zipcode: "",
        phone_number: "",
        website: "",
        image: "",
        banner_image: "",
    });

    const formatPhoneNumber = (value) => {
        const digits = value.replace(/\D/g, '').slice(0, 10);
        const len = digits.length;
        if (len === 0) return '';
        if (len < 3) return `(${digits}`;
        if (len === 3) return `(${digits})`;
        if (len <= 6) return `(${digits.slice(0, 3)})-${digits.slice(3)}`;
        return `(${digits.slice(0, 3)})-${digits.slice(3, 6)}-${digits.slice(6)}`;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setVendorData({ ...vendorData, [name]: name === 'phone_number' ? formatPhoneNumber(value) : value });
    };

    const openTermsModal = () => {
        if (vendorData.password !== vendorData.confirmPassword) {
            setError("Passwords do not match.");
            return;
        }
        const profanityError = checkFieldsForProfanity({
            "Vendor Name": vendorData.vendor_name,
            "Vendor Description": vendorData.vendor_description,
            "First Name": vendorData.first_name,
            "Last Name": vendorData.last_name,
            "Address": vendorData.address,
            "City": vendorData.city,
            "Website": vendorData.website,
        });
        if (profanityError) { setError(profanityError); return; }
        setError("");
        setTermsChecked(false);
        setShowTerms(true);
    };

    const submitAfterTerms = async () => {
        setShowTerms(false);
        try {
            setLoading(true);
            setError("");
            setSuccess("");
            await dispatch(registerVendorUser({
                vendor_name: vendorData.vendor_name,
                vendor_description: vendorData.vendor_description,
                first_name: vendorData.first_name,
                last_name: vendorData.last_name,
                email: vendorData.email,
                password: vendorData.password,
                address: vendorData.address,
                apt_number: vendorData.apt_number,
                city: vendorData.city,
                state: vendorData.state,
                zipcode: vendorData.zipcode,
                mailing_address: vendorData.mailing_address,
                mailing_apt_number: vendorData.mailing_apt_number,
                mailing_city: vendorData.mailing_city,
                mailing_state: vendorData.mailing_state,
                mailing_zipcode: vendorData.mailing_zipcode,
                phone_number: vendorData.phone_number,
                website: vendorData.website,
                image: vendorData.image,
                banner_image: vendorData.banner_image,
                accepted_terms: true,
                terms_version: "2026-06-12",
            })).unwrap();
            navigate("/vendor-dashboard");
        } catch (err) {
            setError("Server error. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const passwordsMatch = vendorData.password === vendorData.confirmPassword;

    return (
        <>
        <section style={{ background: "#f7f8fc", minHeight: "100vh" }} className="py-80">
            <style>{`
                .auth-wrap { position: relative; }
                .auth-icon {
                    position: absolute;
                    left: 14px;
                    top: 50%;
                    transform: translateY(-50%);
                    font-size: 16px;
                    color: #c0c8d8;
                    pointer-events: none;
                    z-index: 1;
                }
                .auth-field {
                    width: 100%;
                    padding: 11px 14px 11px 40px;
                    border: 1.5px solid #e9ecf0;
                    border-radius: 10px;
                    font-size: 14px;
                    color: #1a1a2e;
                    background: #fff;
                    transition: border-color 0.15s, box-shadow 0.15s;
                    outline: none;
                    appearance: none;
                }
                .auth-field:focus {
                    border-color: #2575fc;
                    box-shadow: 0 0 0 3px rgba(37,117,252,0.1);
                }
                .auth-field::placeholder { color: #c4cdd8; }
                .auth-label {
                    display: block;
                    font-size: 11.5px;
                    font-weight: 700;
                    color: #6b7280;
                    letter-spacing: 0.5px;
                    text-transform: uppercase;
                    margin-bottom: 7px;
                }
                .auth-card {
                    background: #fff;
                    border-radius: 20px;
                    box-shadow: 0 4px 32px rgba(0,0,0,0.07);
                    border: 1px solid rgba(0,0,0,0.04);
                }
                .auth-divider {
                    height: 1px;
                    background: linear-gradient(90deg, transparent, #f0f0f0 20%, #f0f0f0 80%, transparent);
                    margin: 24px 0;
                }
                .auth-submit {
                    width: 100%;
                    padding: 13px 24px;
                    border: none;
                    border-radius: 11px;
                    font-size: 15px;
                    font-weight: 700;
                    cursor: pointer;
                    transition: opacity 0.15s, transform 0.1s;
                    letter-spacing: 0.2px;
                }
                .auth-submit:hover:not(:disabled) { opacity: 0.9; transform: translateY(-1px); }
                .auth-submit:disabled { opacity: 0.55; cursor: not-allowed; }
                .auth-error {
                    background: #fef2f2;
                    border: 1px solid #fecaca;
                    color: #dc2626;
                    border-radius: 10px;
                    padding: 11px 14px;
                    font-size: 13.5px;
                    margin-bottom: 18px;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                }
                .auth-section-tag {
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                    background: #eef4ff;
                    color: #2575fc;
                    font-size: 11px;
                    font-weight: 700;
                    letter-spacing: 0.8px;
                    text-transform: uppercase;
                    padding: 5px 12px;
                    border-radius: 99px;
                    margin-bottom: 12px;
                }
                .upload-card {
                    background: #f8f9fb;
                    border: 1.5px dashed #e0e4eb;
                    border-radius: 12px;
                    padding: 20px 20px 16px;
                    transition: border-color 0.15s;
                }
                .upload-card:hover { border-color: #2575fc; }
                .auth-section-heading {
                    font-size: 13px;
                    font-weight: 700;
                    color: #374151;
                    letter-spacing: 0.3px;
                    margin-bottom: 16px;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                }
            `}</style>

            <div className="container container-lg">
                <div className="row justify-content-center">
                    <div className="col-xl-7 col-lg-9">

                        <div className="text-center mb-32">
                            <span className="auth-section-tag">
                                <i className="ph ph-storefront" />
                                Vendor Account
                            </span>
                            <h3 className="fw-bold mb-8" style={{ color: "#0f172a" }}>Become a Vendor</h3>
                            <p style={{ fontSize: 14, color: "#94a3b8", margin: 0 }}>
                                List your business and start reaching customers.{" "}
                                <Link to="/vendor-login" style={{ color: "#2575fc", fontWeight: 600 }}>
                                    Already have an account?
                                </Link>
                            </p>
                        </div>

        <form onSubmit={(e) => e.preventDefault()}>
                            <div className="auth-card p-40">

                                {/* ── Vendor Info ── */}
                                <p className="auth-section-heading">
                                    <i className="ph ph-storefront" style={{ color: "#2575fc" }} />
                                    Vendor Information
                                </p>

                                <div className="mb-20">
                                    <label className="auth-label">
                                        Vendor Name <span style={{ color: "#ef4444" }}>*</span>
                                    </label>
                                    <div className="auth-wrap">
                                        <i className="ph ph-storefront auth-icon" />
                                        <input
                                            type="text"
                                            className="auth-field"
                                            name="vendor_name"
                                            value={vendorData.vendor_name}
                                            onChange={handleChange}
                                            placeholder="Example: Fun Card Media"
                                            maxLength={35}
                                        />
                                    </div>
                                    <p className="mb-0 mt-4" style={{ fontSize: 11.5, color: vendorData.vendor_name.length >= 35 ? "#ef4444" : "#94a3b8", textAlign: "right" }}>
                                        {vendorData.vendor_name.length}/35
                                    </p>
                                </div>

                                <div className="mb-20">
                                    <label className="auth-label">Vendor Description</label>
                                    <textarea
                                        className="auth-field"
                                        style={{ paddingLeft: 14, resize: "vertical", minHeight: 90 }}
                                        name="vendor_description"
                                        value={vendorData.vendor_description}
                                        onChange={handleChange}
                                        placeholder="Describe your business..."
                                        rows="3"
                                        maxLength={255}
                                    />
                                    <p className="mb-0 mt-4" style={{ fontSize: 11.5, color: vendorData.vendor_description.length >= 255 ? "#ef4444" : "#94a3b8", textAlign: "right" }}>
                                        {vendorData.vendor_description.length}/255
                                    </p>
                                </div>

                                <div className="row g-3 mb-20">
                                    <div className="col-md-6">
                                        <label className="auth-label">Website</label>
                                        <div className="auth-wrap">
                                            <i className="ph ph-globe auth-icon" />
                                            <input
                                                type="text"
                                                className="auth-field"
                                                name="website"
                                                value={vendorData.website}
                                                onChange={handleChange}
                                                placeholder="https://yourwebsite.com"
                                            />
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <label className="auth-label">Phone Number</label>
                                        <div className="auth-wrap">
                                            <i className="ph ph-phone auth-icon" />
                                            <input
                                                type="text"
                                                className="auth-field"
                                                name="phone_number"
                                                value={vendorData.phone_number}
                                                onChange={handleChange}
                                                placeholder="(555)-555-5555"
                                            />
                                        </div>
                                        {vendorData.phone_number && (
                                            <p className="mb-0 mt-4 d-flex align-items-center gap-6" style={{
                                                fontSize: 12,
                                                color: vendorData.phone_number.replace(/\D/g, '').length === 10 ? "#10b981" : "#ef4444",
                                                fontWeight: 500,
                                            }}>
                                                <i className={`ph ${vendorData.phone_number.replace(/\D/g, '').length === 10 ? "ph-check-circle" : "ph-warning-circle"}`} style={{ fontSize: 14 }} />
                                                {vendorData.phone_number.replace(/\D/g, '').length === 10 ? "Valid phone number" : `${vendorData.phone_number.replace(/\D/g, '').length}/10 digits`}
                                            </p>
                                        )}
                                    </div>
                                </div>

                                {/* Vendor Icon Photo */}
                                <div className="mb-20">
                                    <label className="auth-label" style={{ marginBottom: 10 }}>Vendor Icon Photo</label>
                                    <div className="upload-card">
                                        <div className="d-flex align-items-center gap-10 mb-14">
                                            <div className="d-flex align-items-center justify-content-center rounded-10 flex-shrink-0" style={{ width: 36, height: 36, background: "#eef4ff" }}>
                                                <i className="ph ph-image" style={{ fontSize: 18, color: "#2575fc" }} />
                                            </div>
                                            <div>
                                                <p className="fw-semibold mb-0" style={{ fontSize: 13, color: "#374151" }}>Vendor Icon</p>
                                                <p className="mb-0" style={{ fontSize: 12, color: "#94a3b8" }}>Appears as your vendor avatar on cards across the site</p>
                                            </div>
                                        </div>
                                        <ImageCropUploader
                                            uploadUrl={`${process.env.REACT_APP_API_URL}/api/upload/vendor-image`}
                                            onUpload={(url) => setVendorData(prev => ({ ...prev, image: url }))}
                                            currentImageUrl={vendorData.image}
                                        />
                                    </div>
                                </div>

                                {/* Vendor Banner Photo */}
                                <div className="mb-20">
                                    <label className="auth-label" style={{ marginBottom: 10 }}>Vendor Card Background Photo</label>
                                    <div className="upload-card">
                                        <div className="d-flex align-items-center gap-10 mb-14">
                                            <div className="d-flex align-items-center justify-content-center rounded-10 flex-shrink-0" style={{ width: 36, height: 36, background: "#f5f3ff" }}>
                                                <i className="ph ph-images" style={{ fontSize: 18, color: "#8b5cf6" }} />
                                            </div>
                                            <div>
                                                <p className="fw-semibold mb-0" style={{ fontSize: 13, color: "#374151" }}>Banner Image</p>
                                                <p className="mb-0" style={{ fontSize: 12, color: "#94a3b8" }}>Background image displayed on your vendor listing card</p>
                                            </div>
                                        </div>
                                        <ImageCropUploader
                                            uploadUrl={`${process.env.REACT_APP_API_URL}/api/upload/vendor-image`}
                                            onUpload={(url) => setVendorData(prev => ({ ...prev, banner_image: url }))}
                                            currentImageUrl={vendorData.banner_image}
                                        />
                                    </div>
                                </div>

                                <div className="auth-divider" />

                                {/* ── Owner Login Info ── */}
                                <p className="auth-section-heading">
                                    <i className="ph ph-user-circle" style={{ color: "#2575fc" }} />
                                    Owner Login Information
                                </p>

                                <div className="row g-3 mb-20">
                                    <div className="col-6">
                                        <label className="auth-label">
                                            First Name <span style={{ color: "#ef4444" }}>*</span>
                                        </label>
                                        <div className="auth-wrap">
                                            <i className="ph ph-user auth-icon" />
                                            <input
                                                type="text"
                                                className="auth-field"
                                                name="first_name"
                                                value={vendorData.first_name}
                                                onChange={handleChange}
                                                placeholder="John"
                                            />
                                        </div>
                                    </div>
                                    <div className="col-6">
                                        <label className="auth-label">
                                            Last Name <span style={{ color: "#ef4444" }}>*</span>
                                        </label>
                                        <div className="auth-wrap">
                                            <i className="ph ph-user auth-icon" />
                                            <input
                                                type="text"
                                                className="auth-field"
                                                name="last_name"
                                                value={vendorData.last_name}
                                                onChange={handleChange}
                                                placeholder="Doe"
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="mb-20">
                                    <label className="auth-label">
                                        Email Address <span style={{ color: "#ef4444" }}>*</span>
                                    </label>
                                    <div className="auth-wrap">
                                        <i className="ph ph-envelope auth-icon" />
                                        <input
                                            type="email"
                                            className="auth-field"
                                            name="email"
                                            value={vendorData.email}
                                            onChange={handleChange}
                                            placeholder="vendor@example.com"
                                        />
                                    </div>
                                </div>

                                <div className="row g-3 mb-20">
                                    <div className="col-md-6">
                                        <label className="auth-label">
                                            Password <span style={{ color: "#ef4444" }}>*</span>
                                        </label>
                                        <div className="auth-wrap">
                                            <i className="ph ph-lock auth-icon" />
                                            <input
                                                type="password"
                                                className="auth-field"
                                                name="password"
                                                value={vendorData.password}
                                                onChange={handleChange}
                                                placeholder="••••••••"
                                            />
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <label className="auth-label">
                                            Re-enter Password <span style={{ color: "#ef4444" }}>*</span>
                                        </label>
                                        <div className="auth-wrap">
                                            <i className="ph ph-lock-key auth-icon" />
                                            <input
                                                type="password"
                                                className="auth-field"
                                                name="confirmPassword"
                                                value={vendorData.confirmPassword}
                                                onChange={handleChange}
                                                placeholder="••••••••"
                                                style={{
                                                    borderColor: vendorData.confirmPassword
                                                        ? passwordsMatch ? "#10b981" : "#ef4444"
                                                        : "",
                                                }}
                                            />
                                        </div>
                                        {vendorData.confirmPassword && (
                                            <p className="mb-0 mt-6 d-flex align-items-center gap-6" style={{
                                                fontSize: 12,
                                                color: passwordsMatch ? "#10b981" : "#ef4444",
                                                fontWeight: 500,
                                            }}>
                                                <i className={`ph ${passwordsMatch ? "ph-check-circle" : "ph-x-circle"}`} style={{ fontSize: 14 }} />
                                                {passwordsMatch ? "Passwords match" : "Passwords do not match"}
                                            </p>
                                        )}
                                    </div>
                                </div>

                                <div className="auth-divider" />

                                {/* ── Vendor Address ── */}
                                <p className="auth-section-heading">
                                    <i className="ph ph-map-pin" style={{ color: "#2575fc" }} />
                                    Vendor Address
                                </p>

                                <div className="mb-20">
                                    <label className="auth-label">Address</label>
                                    <div className="auth-wrap">
                                        <i className="ph ph-map-pin auth-icon" />
                                        <input
                                            type="text"
                                            className="auth-field"
                                            name="address"
                                            value={vendorData.address}
                                            onChange={handleChange}
                                            placeholder="123 Main Street"
                                        />
                                    </div>
                                </div>

                                <div className="row g-3 mb-20">
                                    <div className="col-4">
                                        <label className="auth-label">Apt #</label>
                                        <div className="auth-wrap">
                                            <i className="ph ph-door auth-icon" />
                                            <input
                                                type="text"
                                                className="auth-field"
                                                name="apt_number"
                                                value={vendorData.apt_number}
                                                onChange={handleChange}
                                                placeholder="Apt 4B"
                                            />
                                        </div>
                                    </div>
                                    <div className="col-8">
                                        <label className="auth-label">City</label>
                                        <div className="auth-wrap">
                                            <i className="ph ph-buildings auth-icon" />
                                            <input
                                                type="text"
                                                className="auth-field"
                                                name="city"
                                                value={vendorData.city}
                                                onChange={handleChange}
                                                placeholder="City"
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="row g-3 mb-20">
                                    <div className="col-4">
                                        <label className="auth-label">State</label>
                                        <select
                                            className="auth-field"
                                            name="state"
                                            value={vendorData.state}
                                            onChange={handleChange}
                                            style={{ paddingLeft: 14, appearance: 'auto' }}
                                        >
                                            <option value="">State</option>
                                            {US_STATES.map(s => (
                                                <option key={s.value} value={s.value}>{s.label}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="col-8">
                                        <label className="auth-label">Zipcode</label>
                                        <div className="auth-wrap">
                                            <i className="ph ph-map-trifold auth-icon" />
                                            <input
                                                type="text"
                                                className="auth-field"
                                                name="zipcode"
                                                value={vendorData.zipcode}
                                                onChange={handleChange}
                                                placeholder="34145"
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="auth-divider" />

                                {/* ── Mailing Address ── */}
                                <p className="auth-section-heading">
                                    <i className="ph ph-envelope-simple" style={{ color: "#2575fc" }} />
                                    Mailing Address
                                </p>

                                <div className="mb-20">
                                    <label className="auth-label">Mailing Address</label>
                                    <div className="auth-wrap">
                                        <i className="ph ph-map-pin auth-icon" />
                                        <input
                                            type="text"
                                            className="auth-field"
                                            name="mailing_address"
                                            value={vendorData.mailing_address}
                                            onChange={handleChange}
                                            placeholder="Mailing Address"
                                        />
                                    </div>
                                </div>

                                <div className="row g-3 mb-20">
                                    <div className="col-4">
                                        <label className="auth-label">Apt #</label>
                                        <div className="auth-wrap">
                                            <i className="ph ph-door auth-icon" />
                                            <input
                                                type="text"
                                                className="auth-field"
                                                name="mailing_apt_number"
                                                value={vendorData.mailing_apt_number}
                                                onChange={handleChange}
                                                placeholder="Apt 4B"
                                            />
                                        </div>
                                    </div>
                                    <div className="col-8">
                                        <label className="auth-label">City</label>
                                        <div className="auth-wrap">
                                            <i className="ph ph-buildings auth-icon" />
                                            <input
                                                type="text"
                                                className="auth-field"
                                                name="mailing_city"
                                                value={vendorData.mailing_city}
                                                onChange={handleChange}
                                                placeholder="Mailing City"
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="row g-3 mb-28">
                                    <div className="col-4">
                                        <label className="auth-label">State</label>
                                        <select
                                            className="auth-field"
                                            name="mailing_state"
                                            value={vendorData.mailing_state}
                                            onChange={handleChange}
                                            style={{ paddingLeft: 14, appearance: 'auto' }}
                                        >
                                            <option value="">State</option>
                                            {US_STATES.map(s => (
                                                <option key={s.value} value={s.value}>{s.label}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="col-8">
                                        <label className="auth-label">Zipcode</label>
                                        <div className="auth-wrap">
                                            <i className="ph ph-map-trifold auth-icon" />
                                            <input
                                                type="text"
                                                className="auth-field"
                                                name="mailing_zipcode"
                                                value={vendorData.mailing_zipcode}
                                                onChange={handleChange}
                                                placeholder="Mailing Zipcode"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {error && (
                                    <div className="auth-error">
                                        <i className="ph ph-warning-circle" style={{ fontSize: 16, flexShrink: 0 }} />
                                        {error}
                                    </div>
                                )}

                                <button
                                    type="button"
                                    className="auth-submit"
                                    style={{ background: "#6B9FD4", color: "#fff" }}
                                    disabled={loading || (vendorData.confirmPassword.length > 0 && !passwordsMatch)}
                                    onClick={openTermsModal}
                                >
                                    {loading ? "Creating Vendor Account..." : "Create Vendor Account"}
                                </button>

                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </section>

        {showTerms && ReactDOM.createPortal(
            <div
                onClick={(e) => { if (e.target === e.currentTarget) setShowTerms(false); }}
                style={{
                    position: 'fixed', inset: 0,
                    background: 'rgba(0,0,0,0.55)',
                    zIndex: 99999,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    padding: 20,
                }}
            >
                <div style={{
                    background: '#fff', borderRadius: 18,
                    boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
                    width: '100%', maxWidth: 680, maxHeight: '90vh',
                    display: 'flex', flexDirection: 'column', overflow: 'hidden',
                }}>
                    {/* Header */}
                    <div style={{ padding: '24px 28px 16px', borderBottom: '1px solid #f0f0f0', flexShrink: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <div style={{ width: 38, height: 38, background: '#eef4ff', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                <i className="ph ph-file-text" style={{ fontSize: 20, color: '#2575fc' }} />
                            </div>
                            <div>
                                <h5 style={{ fontWeight: 700, margin: 0, fontSize: 18, color: '#0f172a' }}>Terms of Service &amp; Policies</h5>
                                <p style={{ margin: 0, fontSize: 12, color: '#94a3b8' }}>Please read and agree before creating your account</p>
                            </div>
                        </div>
                    </div>

                    {/* Scrollable text */}
                    <div style={{
                        flex: 1, overflowY: 'auto', padding: '20px 28px',
                        background: '#f8f9fb', fontSize: 13, lineHeight: 1.75,
                        color: '#374151', whiteSpace: 'pre-wrap',
                        fontFamily: "'Courier New', Courier, monospace",
                        borderTop: '1px solid #e9ecf0', borderBottom: '1px solid #e9ecf0',
                    }}>
                        {TERMS_TEXT}
                    </div>

                    {/* Footer */}
                    <div style={{ padding: '20px 28px 24px', flexShrink: 0, background: '#fff' }}>
                        <label
                            htmlFor="vendor-terms-checkbox"
                            style={{
                                display: 'flex', alignItems: 'flex-start', gap: 12,
                                padding: '14px 16px', background: '#f0f6ff',
                                border: '1.5px solid #c3d9ff', borderRadius: 10,
                                marginBottom: 16, cursor: 'pointer',
                            }}
                        >
                            <input
                                type="checkbox"
                                id="vendor-terms-checkbox"
                                checked={termsChecked}
                                onChange={(e) => setTermsChecked(e.target.checked)}
                                style={{ width: 18, height: 18, flexShrink: 0, marginTop: 2, cursor: 'pointer', accentColor: '#2575fc' }}
                            />
                            <span style={{ fontSize: 13, color: '#1e3a6e', lineHeight: 1.5, fontWeight: 500, margin: 0 }}>
                                By creating an account, I confirm that I have read and agree to the Fun Card Deals Terms of Service and related policies, and I acknowledge that I am at least 18 years of age.
                            </span>
                        </label>

                        <button
                            type="button"
                            onClick={() => { if (termsChecked) submitAfterTerms(); }}
                            disabled={!termsChecked}
                            style={{
                                width: '100%', padding: '13px 24px', border: 'none',
                                borderRadius: 11, fontSize: 15, fontWeight: 700,
                                cursor: termsChecked ? 'pointer' : 'not-allowed',
                                background: '#6B9FD4', color: '#fff',
                                opacity: termsChecked ? 1 : 0.45,
                                marginBottom: 0,
                            }}
                        >
                            Agree and Create Vendor Account
                        </button>

                        <button
                            type="button"
                            onClick={() => setShowTerms(false)}
                            style={{
                                width: '100%', padding: '10px 24px', marginTop: 10,
                                border: '1.5px solid #e9ecf0', borderRadius: 11,
                                fontSize: 14, fontWeight: 600, cursor: 'pointer',
                                background: 'transparent', color: '#6b7280',
                            }}
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            </div>,
            document.body
        )}
        </>
    );
};

export default VendorRegistration;
